import { Chip } from '@mui/material';

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const TONE_PALETTE_KEY = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'error',
} as const;

interface StatusChipProps {
  label: string;
  tone: StatusTone;
}

/**
 * Badge/pill variant per hybrid-3c.md v3.2: `neutral` renders the theme's
 * default chip fill (the warm outline system — never restyled locally); the
 * other tones use a soft tint of the matching palette hue (fill `${main}14`,
 * `warning` `1F`) with the border/text in the `.dark` shade for contrast.
 * Colours come from the theme palette only — one status accent leads per
 * view; callers map a domain enum to one of these five tones, never a
 * bespoke colour.
 */
export function StatusChip({ label, tone }: StatusChipProps) {
  if (tone === 'neutral') {
    return <Chip label={label} size="small" />;
  }

  const paletteKey = TONE_PALETTE_KEY[tone];

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: (theme) => `${theme.palette[paletteKey].main}${paletteKey === 'warning' ? '1F' : '14'}`,
        color: (theme) => theme.palette[paletteKey].dark,
        border: (theme) => `1px solid ${theme.palette[paletteKey].main}`,
      }}
    />
  );
}
